/**
 * The onboarding view's tabs, in order. `flag` is the organization_carrier_statuses column that puts a carrier in the
 * tab, and `subtitle` is the short description shown above its list. The class strings tint the tab and its panel.
 */
export const BUCKETS = [
    {
        key: 'ACCOUNT_SETUP',
        label: 'Account Setup',
        flag: 'in_account_setup',
        subtitle: 'Flex Center, Fulfillment Partner and Motor Carrier accounts that still need to be set up',
        tabClass: 'border-slate-300 bg-white text-slate-900',
        panelClass: 'border-slate-300 bg-white',
    },
    {
        key: 'COMPLIANCE',
        label: 'Compliance',
        flag: 'in_compliance',
        subtitle: 'All Motor Carriers, for compliance to review, approve or deny',
        tabClass: 'border-blue-300 bg-blue-50 text-blue-800',
        panelClass: 'border-blue-300 bg-blue-50',
    },
    {
        key: 'INTEGRATIONS',
        label: 'Integrations',
        flag: 'in_integrations',
        subtitle: "Flex Centers and Fulfillment Partners whose integrations aren't live yet",
        tabClass: 'border-violet-300 bg-violet-50 text-violet-800',
        panelClass: 'border-violet-300 bg-violet-50',
    },
    {
        key: 'LIVE',
        label: 'Live',
        flag: 'is_live',
        subtitle: 'All live Motor Carriers, Fulfillment Partners, and Flex Centers',
        tabClass: 'border-green-300 bg-green-50 text-green-800',
        panelClass: 'border-green-300 bg-green-50',
    },
];

/** Card subtitle for each organization_carrier_statuses `relationship`. */
export const RELATIONSHIP_SUBTITLES = {
    OWNED: 'Flex Center',
    PARTNER: 'Fulfillment Partner',
    CONTRACTOR: 'Motor Carrier',
};

/** Badge colours in the order the info dialog lists them: green, blue, purple, yellow, red. */
export const BADGE_VARIANT_ORDER = ['primary', 'info', 'accent', 'warning', 'destructive'];

/**
 * Card badges, in display order. `flag` is the view column that shows the badge; `variant` is the design-system Tag
 * variant.
 */
export const BADGES = [
    { key: 'INVITED', label: 'Invited', flag: 'badge_invited', variant: 'warning' },
    { key: 'REQUESTED_DOCUMENTS', label: 'Requested Documents', flag: 'badge_requested_documents', variant: 'warning' },
    { key: 'IN_COMPLIANCE', label: 'In Compliance', flag: 'badge_in_compliance', variant: 'info' },
    { key: 'APPROVED', label: 'Approved By Compliance', flag: 'badge_approved', variant: 'primary' },
    { key: 'REJECTED', label: 'Rejected By Compliance', flag: 'badge_rejected', variant: 'destructive' },
    { key: 'NEEDS_PRICING', label: 'Needs Pricing', flag: 'badge_needs_pricing', variant: 'warning' },
    { key: 'INTEGRATION', label: 'Integration Not Live', flag: 'badge_integration_pending', variant: 'warning' },
    {
        key: 'NO_INTEGRATIONS',
        label: 'Integrations Not Required',
        flag: 'badge_no_integrations',
        variant: 'accent',
    },
    { key: 'LIVE', label: 'Live', flag: 'badge_live', variant: 'primary' },
];

/** The shipper onboarding view's tabs, in order, shaped like BUCKETS; `flag` is an organization_shipper_statuses column. */
export const SHIPPER_BUCKETS = [
    {
        key: 'ACCOUNT_SETUP',
        label: 'Account Setup',
        flag: 'in_account_setup',
        subtitle: 'Shippers that still need pricing, connected carriers or a delivery profile',
        tabClass: 'border-slate-300 bg-white text-slate-900',
        panelClass: 'border-slate-300 bg-white',
    },
    {
        key: 'INTEGRATIONS',
        label: 'Integrations',
        flag: 'in_integrations',
        subtitle: "Shippers with no integration set up, or one that's failing or needs info",
        tabClass: 'border-violet-300 bg-violet-50 text-violet-800',
        panelClass: 'border-violet-300 bg-violet-50',
    },
    {
        key: 'LIVE',
        label: 'Live',
        flag: 'is_live',
        subtitle: 'Shippers that are set up and live',
        tabClass: 'border-green-300 bg-green-50 text-green-800',
        panelClass: 'border-green-300 bg-green-50',
    },
];

/** Shipper card badges, in display order, shaped like BADGES. */
export const SHIPPER_BADGES = [
    {
        key: 'NO_INTEGRATIONS_SETUP',
        label: 'No Integrations Setup',
        flag: 'badge_no_integrations_setup',
        variant: 'warning',
    },
    {
        key: 'INTEGRATION_FAILING',
        label: 'Failing Integrations',
        flag: 'badge_integration_failing',
        variant: 'destructive',
    },
    {
        key: 'INTEGRATION_NEEDS_INFO',
        label: 'Integration Needs Info',
        flag: 'badge_integration_needs_info',
        variant: 'warning',
    },
    { key: 'NO_PRICING', label: 'No Pricing Setup', flag: 'badge_no_pricing', variant: 'warning' },
    {
        key: 'NO_CONNECTED_CARRIERS',
        label: 'No Connected Carriers',
        flag: 'badge_no_connected_carriers',
        variant: 'warning',
    },
    {
        key: 'CARRIERS_MISSING_PRICING',
        label: 'Carriers Missing Pricing',
        flag: 'badge_carriers_missing_pricing',
        variant: 'warning',
    },
    { key: 'NO_DELIVERY_PROFILE', label: 'No Delivery Profile', flag: 'badge_no_delivery_profile', variant: 'warning' },
    {
        key: 'NO_INTEGRATIONS',
        label: 'Integrations Not Required',
        flag: 'badge_no_integrations',
        variant: 'accent',
    },
    { key: 'NO_ORDERS', label: 'No Orders Yet', flag: 'badge_no_orders', variant: 'info' },
    { key: 'LIVE', label: 'Live', flag: 'badge_live', variant: 'primary' },
];

/** The badge for each status a Motor Carrier's Flex Center partnership carries. */
export const COMPLIANCE_STATUS_BADGES = {
    PENDING: BADGES.find((badge) => badge.key === 'IN_COMPLIANCE'),
    APPROVED: BADGES.find((badge) => badge.key === 'APPROVED'),
    DENIED: BADGES.find((badge) => badge.key === 'REJECTED'),
};

/** Integration badge label for each `integration_badge_status` that isn't LIVE. */
export const INTEGRATION_BADGE_LABELS = {
    NOT_STARTED: 'Integration Not Started',
    FAILING: 'Integration Failing',
    NEEDS_INFO: 'Integration Needs Info',
    REQUEST_SENT: 'Integration Requested',
    OPENED: 'Integration Opened',
    DETAILS_RECEIVED: 'Integration Details Received',
    TESTING: 'Integration Testing',
    ENGINEER_REVIEW: 'Integration Engineer Review',
};

/** Compliance decisions a reviewer can make, keyed by the status the review writes. */
export const REVIEW_DECISIONS = {
    APPROVED: { verb: 'Approve', view: 'View Approval' },
    DENIED: { verb: 'Deny', view: 'View Rejection' },
};

/**
 * A PENDING compliance review: written by the database, not a reviewer, when a compliance document is uploaded for an
 * approved contractor. Shown in the history only.
 */
export const SENT_BACK_FOR_REVIEW = { label: 'Sent Back For Review', variant: 'info' };
